import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationBulkSendInterface, NotificationInterface, NotificationTemplateInterface } from '../models';
import { LoadNotifications, SendNotifications, UpdateNotification, UpdateNotificationsReadState } from '../state/actions';
import { DeleteNotificationTemplate } from '../state/actions/notification-delete.action';
import { LoadNotificationTemplates } from '../state/actions/notification-load.action';
import { CreateNotificationTemplate, NotificationBulkUpdatePayload, UpdateNotificationTemplate } from '../state/actions/notification-update.action';

import { NotificationServiceInterface } from './notification-service.interface';

export class NotificationServiceFilters {

  public subjectId?: number;

  constructor({
    subjectId = undefined,
  }: NotificationServiceFilters) {
    this.subjectId = subjectId;
  }
}

@Injectable()
export class NotificationService implements NotificationServiceInterface {

  public createNotificationEvent: Subject<number[]> = new Subject<number[]>();

  constructor(private readonly _store: Store<AppStateInterface>) {
    this.onInit();
  }

  public onInit(): void {}

  public openNotificationDialog(ids: number[]): void {
    this.createNotificationEvent.next(ids);
  }

  public getNotifications(): Observable<Readonly<NotificationInterface[]>> {

    return this._store.select(({ notification }: AppStateInterface) => notification.notifications)
    .pipe(
      map((storeNotifications: {[id: number]:NotificationInterface}) => {
        return this.mapToArray(storeNotifications);
      }),
    );
  }

  public getUnreadNotificationCount(): Observable<Readonly<number>> {
    return this._store.select(({ notification }: AppStateInterface) => notification.notifications)
    .pipe(
      map((storeNotifications: {[id: number]:NotificationInterface}) => {
        return this.mapToArray(storeNotifications).filter((notification: NotificationInterface) => !notification.read).length;
      }),
    );
  }

  public loadAll(filters?: NotificationServiceFilters): void {
    this._store.dispatch(new LoadNotifications(filters));
  }

  public update(notificationToUpdate: NotificationInterface): Observable<NotificationInterface> {
    this._store.dispatch(new UpdateNotification(notificationToUpdate));

    return this._store.select(({ notification }:AppStateInterface) => notification.notifications[notificationToUpdate.id]);
  }

  public updateManyReadUnread(notificationsToUpdate: NotificationInterface[], read: boolean): void {
    const readStateUpdatePayload: NotificationBulkUpdatePayload = new NotificationBulkUpdatePayload({
      readState: read,
      notifications: notificationsToUpdate,
    });
    this._store.dispatch(new UpdateNotificationsReadState(readStateUpdatePayload));
  }

  public getNotificationTemplates(): Observable<NotificationTemplateInterface[]> {
    this._store.dispatch(new LoadNotificationTemplates());

    return this._store.select(({ notification }: AppStateInterface) => notification ? notification.notificationTemplates : undefined);
  }

  public sendNotifications(notificationBulkSendPayload: NotificationBulkSendInterface): void {
    this._store.dispatch(new SendNotifications(notificationBulkSendPayload));
  }

  public sendNotificationResults(): Observable<number> {
    return this._store.select(({ notification }: AppStateInterface) => notification.sendResults);
  }

  public createNotificationTemplate(templateToCreate: NotificationTemplateInterface): Observable<NotificationTemplateInterface> {
    this._store.dispatch(new CreateNotificationTemplate(templateToCreate));

    return this._store.select(({ notification }: AppStateInterface) => notification.selectedTemplate);
  }

  public updateNotificationTemplate(templateToUpdate: NotificationTemplateInterface): Observable<NotificationTemplateInterface> {
    this._store.dispatch(new UpdateNotificationTemplate(templateToUpdate));

    return this._store.select(({ notification }: AppStateInterface) => notification.selectedTemplate);
  }

  public deleteNotificationTemplate(templateToDelete: NotificationTemplateInterface): Observable<NotificationTemplateInterface> {
    this._store.dispatch(new DeleteNotificationTemplate(templateToDelete));

    return this._store.select(({ notification }: AppStateInterface) => notification.selectedTemplate);
  }

  private mapToArray(mapNotifications: {[id: number]:NotificationInterface}): NotificationInterface[] {
    const resultNotifications: NotificationInterface[] = [];

    for(const key in mapNotifications) {
      if(mapNotifications.hasOwnProperty(key)) {
        resultNotifications.push(mapNotifications[key]);
      }

    }
    return resultNotifications;
  }
}
